/**
 * Atlas Web — application shell.
 *
 * Top bar (menu, global search, tenant switcher, inbox, help, user menu),
 * role-filtered sidebar (navigation config below), command palette
 * (Cmd+K / Ctrl+K), and the routed content area. The command palette is
 * a Linear/Slack-style overlay: commands, navigation, record lookup.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/lib/auth/store";
import { useApiQuery } from "@/lib/api/hooks";
import { signOut } from "@/lib/auth/sign-out";
import { Badge, Button } from "@/components/ui";
import { Icon } from "@/components/icons";
import { CommandPalette } from "@/components/CommandPalette";
import { NAV_ENTRIES } from "@/components/nav-data";
import { isNotAvailable, visibleNav } from "@/components/nav-model";
import { NotAvailableTag } from "@/components/NotAvailable";
import { useTheme } from "@/theme";
import { navLabel, useTranslation } from "@/lib/i18n";

export type NavEntry = (typeof NAV_ENTRIES)[number];

/**
 * The sidebar reads nav-data, which is what nav-data says it is for:
 * "Single source of truth for module paths".
 *
 * It was not. This file carried its own copy of the whole tree, so the
 * command palette (which does read nav-data) and the sidebar were two
 * independent lists that had already drifted — six sections existed in one
 * and not the other. Adding a module meant editing two files and nothing
 * told you when you edited only one.
 */
const NAV: NavEntry[] = NAV_ENTRIES;

function Sidebar({
  onNavigate,
  isOpen,
}: {
  onNavigate: () => void;
  isOpen: boolean;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  // Children were never filtered, so a section the user could open listed
  // every screen inside it regardless of role — the menu promised things the
  // router now refuses. The command palette applies the same filter.
  const entries = useMemo(
    () => visibleNav(hasPermission, NAV),
    [hasPermission],
  );

  return (
    <aside
      className={`atlas-sidebar${isOpen ? " atlas-sidebar--open" : ""}`}
      aria-label={t("shell.primaryNav")}
    >
      <div className="atlas-sidebar__brand">
        <img src="/icons/atlas.svg" alt="" width="28" height="28" />
        <span>ATLAS</span>
      </div>
      <nav className="atlas-sidebar__nav">
        {entries.map((entry) => {
          const isSection = (entry.children?.length ?? 0) > 0;
          const active = isSection
            ? pathname.startsWith(entry.path)
            : pathname === entry.path;
          return (
            <div key={entry.label}>
              <NavLink
                to={entry.path}
                className={`atlas-sidebar__item${active ? " is-active" : ""}`}
                onClick={onNavigate}
              >
                <span>
                  {navLabel(t, entry.label)}
                  {isNotAvailable(entry) && (
                    <>
                      {" "}
                      <NotAvailableTag />
                    </>
                  )}
                </span>
              </NavLink>
              {isSection && active && (
                <div style={{ paddingInlineStart: 16, marginBlockStart: 2 }}>
                  {entry.children!.map((child) => {
                    const childActive = pathname === child.path;
                    return (
                      <NavLink
                        key={child.label}
                        to={child.path}
                        className={`atlas-sidebar__item${childActive ? " is-active" : ""}`}
                        onClick={onNavigate}
                        style={{ fontSize: 13, padding: "5px 10px" }}
                      >
                        {navLabel(t, child.label)}
                        {isNotAvailable(child) && (
                          <>
                            {" "}
                            <NotAvailableTag />
                          </>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
      <div style={{ padding: 12 }}>
        <Badge tone="primary">
          {t("shell.cell", {
            cell: useAuthStore.getState().claims?.cell_id ?? "—",
          })}
        </Badge>
      </div>
    </aside>
  );
}

/** The fields of GET /v1/users/me the header reads. */
interface Me {
  displayName?: unknown;
  email?: unknown;
}

const filled = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value.trim() : null;

/**
 * Who is signed in, for the header's profile button: the display name, then
 * the email, from GET /v1/users/me (the query Profile and the locale sync
 * already use, so no second request); until it answers, or if it fails, the
 * word Profile. Never the token's `sub`: the header used to show
 * `claims.sub.slice(0, 8)`, the person's user id prefix, because it never
 * read /users/me at all (D1043).
 */
export function personLabel(me: Me | undefined): string | null {
  return filled(me?.displayName) ?? filled(me?.email);
}

function TopBar({
  onOpenPalette,
  onToggleSidebar,
}: {
  onOpenPalette: () => void;
  onToggleSidebar: () => void;
}) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const activeTenantCode = useAuthStore((s) => s.activeTenantCode);
  const [signingOut, setSigningOut] = useState(false);
  const { resolved, toggle } = useTheme();
  const { data: me } = useApiQuery<Me>(["me"], "/users/me", {
    staleTime: 15_000,
  });
  const person = personLabel(me);

  // Server first, then local state; signOut clears locally even when the
  // call fails, so this always lands on /login (D6).
  const onSignOut = async () => {
    setSigningOut(true);
    await signOut();
    navigate("/login", { replace: true });
  };
  void pathname;

  return (
    <header className="atlas-topbar">
      <button
        type="button"
        className="atlas-btn atlas-btn--sm atlas-topbar__menu"
        onClick={onToggleSidebar}
        aria-label={t("shell.toggleNav")}
      >
        <Icon name="menu" size={18} />
      </button>
      <button
        type="button"
        className="atlas-btn atlas-btn--sm"
        onClick={onOpenPalette}
      >
        <Icon name="command" size={16} /> {t("shell.commands")}{" "}
        <kbd className="atlas-kbd">⌘K</kbd>
      </button>
      <div style={{ flex: 1 }} />
      <button
        type="button"
        className="atlas-btn atlas-btn--sm"
        onClick={toggle}
        aria-label={
          resolved === "dark" ? t("shell.themeToLight") : t("shell.themeToDark")
        }
      >
        <Icon name={resolved === "dark" ? "sun" : "moon"} size={16} />
      </button>
      <span className="atlas-mono" style={{ fontSize: 13 }}>
        {activeTenantCode ?? t("shell.noWorkspace")}
      </span>
      <NavLink to="/app/inbox" className="atlas-btn atlas-btn--sm">
        <Icon name="inbox" size={16} /> {t("nav.inbox")}
      </NavLink>
      <NavLink
        to="/app/notifications"
        className="atlas-btn atlas-btn--sm"
        aria-label={t("nav.notifications")}
      >
        <Icon name="bell" size={16} />
      </NavLink>
      <Button
        size="sm"
        variant="ghost"
        title={t("nav.profile")}
        onClick={() => void navigate("/app/profile")}
      >
        <Icon name="user" size={16} />{" "}
        {person ? <bdi>{person}</bdi> : t("nav.profile")}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        disabled={signingOut}
        onClick={() => void onSignOut()}
      >
        <Icon name="logout" size={16} />{" "}
        {signingOut ? t("session.signingOut") : t("session.signOut")}
      </Button>
    </header>
  );
}

export function AppShell({ children }: { children?: ReactNode }) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  void navigate;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="atlas-app">
      <Sidebar onNavigate={() => setSidebarOpen(false)} isOpen={sidebarOpen} />
      <div className="atlas-main">
        <TopBar
          onOpenPalette={() => setPaletteOpen(true)}
          onToggleSidebar={() => setSidebarOpen((o) => !o)}
        />
        <main className="atlas-content" id="main">
          {children ?? <Outlet />}
        </main>
      </div>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />
    </div>
  );
}
